import { Box, Stack } from '@mui/material';
import SliderContent from '@/components/SliderContent';
import Paper from '@/components/Paper';
import useResponsive from '@/hooks/useResponsive';
import Typography from '@/components/Typography';
import Image from 'next/image';
import { ReactNode } from 'react';
import Backdrop from '../../../public/assets/market_backdrop.svg';
import map from 'lodash/map';
import useGetCrypto, { keyType } from '@/hooks/useGetCrypto';
import useLiveCrypto from '@/hooks/useLiveCrypto';
import numberUtils from '@/utils/numberUtils';

type TableProps = {
  primaryTitle: string;
  secondaryTitle?: string;
  children: ReactNode[] | JSX.Element[];
};

const Table = (props: TableProps) => {
  const { primaryTitle, secondaryTitle, children } = props;
  return (
    <Paper
      elevation={0}
      sx={{
        width: { xs: 'auto', lg: 288 },
        height: { xs: '100%', lg: 'unset' },
        borderRadius: 3,
        flex: 1,
        py: 4,
        px: 4.5
      }}
    >
      <Stack>
        <Stack
          mb={3}
          gap={1}
          height={44}
          borderRadius={3}
          sx={theme => ({ background: theme.palette.secondary.A60 })}
          flexDirection="row"
          alignItems="center"
          justifyContent="center"
        >
          <Typography variant="body2" weight="bold" color="textColor.A80">
            {primaryTitle}
          </Typography>
          {secondaryTitle && (
            <Typography variant="body3" color="textColor.A60">
              {secondaryTitle}
            </Typography>
          )}
        </Stack>
        {children}
      </Stack>
    </Paper>
  );
};

type RowProps = {
  pingPong?: boolean;
  image: string;
  symbol: string;
  base: string;
  name: string;
  price: string;
  changePrice: number;
  source: 'p2p' | 'otc';
};

const Row = (props: RowProps) => {
  const {
    changePrice,
    image,
    name,
    price,
    source,
    base,
    symbol,
    pingPong = false
  } = props;
  const priceKey = source === 'otc' ? 'midPrice' : 'price';
  const { message, isConnected } = useLiveCrypto({
    source,
    symbol,
    connect: true,
    pingPong
  });

  const handleColorText = (change: number) => {
    if (change < 0) return 'error.A100';
    if (change > 0) return 'success.A100';
    return 'textColor.A80';
  };

  const change = Number(
    (isConnected && !!message?.priceChange
      ? message.priceChange
      : changePrice) || 0
  );
  return (
    <a href={`/price/${base.toLowerCase()}`}>
      <Stack
        flexDirection="row"
        alignItems="center"
        px={3}
        py={2}
        sx={theme => ({
          borderBottom: `1px solid ${theme.palette.secondary.A100}`,
          '&:last-child': {
            border: 0
          }
        })}
      >
        <Stack flexDirection="row" flex={1} gap={1.5} justifyContent="start">
          <Image src={image} width={19} height={19} alt={name} />
          <Typography weight="bold" variant="body3" noWrap maxWidth={65}>
            {name}
          </Typography>
        </Stack>
        <Stack flexDirection="row" flex={1} gap={0.5} justifyContent="end">
          <Typography
            dir="ltr"
            color={handleColorText(change)}
            variant="body4"
            weight="bold"
          >
            ({change > 0 ? `+${change}` : change})
          </Typography>
          <Typography
            noWrap
            maxWidth={80}
            dir="ltr"
            weight="bold"
            variant="body3"
            color="textColor.A60"
          >
            {isConnected && !!message?.[priceKey] ? message[priceKey] : price}
          </Typography>
          <Typography variant="body4" color="textColor.A50">
            تومان
          </Typography>
        </Stack>
      </Stack>
    </a>
  );
};

const TrendCrypto = () => {
  const source = 'p2p';
  const { trends } = useGetCrypto(
    {
      trends: { side: source, quote: 'TMN', count: 3 }
    },
    'getCryptoList',
    []
  );
  return (
    <Table primaryTitle="بیشترین معامله روزانه" secondaryTitle="(۲۴ ساعته)">
      {map(trends, (item, index) => (
        <Row
          key={item.id}
          source={source}
          pingPong={index === 0}
          image={item.logoUrl || ''}
          symbol={item.symbol}
          base={item.base}
          name={item.baseFa}
          changePrice={Number(item[source]?.priceChange || 0)}
          price={item.p2p?.price || '-'}
        />
      ))}
    </Table>
  );
};
const TopGainers = () => {
  const source = 'otc';
  const { topGainers } = useGetCrypto(
    {
      topGainers: { side: source, count: 3, quote: 'TMN' }
    },
    'getCryptoList',
    []
  );
  return (
    <Table primaryTitle="بیشترین صعود روزانه" secondaryTitle="(۲۴ ساعته)">
      {map(topGainers, item => (
        <Row
          key={item.id}
          source={source}
          image={item.logoUrl || ''}
          symbol={item.symbol}
          base={item.base}
          name={item.baseFa}
          changePrice={Number(item[source]?.priceChange || 0)}
          price={item.otc?.midPrice || '-'}
        />
      ))}
    </Table>
  );
};
const TopLosers = () => {
  const source = 'otc';
  const { topLosers } = useGetCrypto(
    {
      topLosers: { side: source, count: 3, quote: 'TMN' }
    },
    'getCryptoList',
    []
  );
  return (
    <Table primaryTitle="بیشترین ریزش روزانه" secondaryTitle="(۲۴ ساعته)">
      {map(topLosers, item => (
        <Row
          key={item.id}
          source={source}
          image={item.logoUrl || ''}
          symbol={item.symbol}
          base={item.base}
          name={item.baseFa}
          changePrice={Number(item[source]?.priceChange || 0)}
          price={item.otc?.midPrice || '-'}
        />
      ))}
    </Table>
  );
};

const RecentlyAdded = () => {
  const { recentlyAdded } = useGetCrypto(
    { recentlyAdded: 3 },
    'getCryptoList',
    []
  );
  const items = require(`../../../public/json/market/recentlyAdded.json`);

  return (
    <Table primaryTitle="ارزهای تازه اضافه شده">
      {map(recentlyAdded || [], item => {
        let source: keyType = items[item.symbol];
        return (
          <Row
            key={item.id}
            source={source}
            image={item.logoUrl || ''}
            symbol={item.symbol}
            base={item.base}
            name={item.baseFa}
            changePrice={Number(item[source]?.priceChange || 0)}
            price={
              (source === 'otc' ? item.otc?.midPrice : item.p2p?.price) || '-'
            }
          />
        );
      })}
    </Table>
  );
};

const Trending = () => {
  const { matches } = useResponsive();

  return (
    <Stack mb={matches({ xs: 6, lg: 24 })} position="relative">
      <Box
        sx={{
          width: matches({ xs: 375, lg: 582 }),
          height: matches({ xs: 375, lg: 582 }),
          zIndex: 1,
          opacity: 0.1,
          borderRadius: matches({ xs: 93.75, lg: 145.5 }),
          bottom: matches({ xs: 107, lg: -138 }),
          left: matches({ xs: 0, lg: -76 }),
          position: 'absolute',
          background: '#717BEB',
          filter: matches({ xs: 'blur(83.521px)', lg: 'blur(101.875px)' })
        }}
      />
      <Stack
        gap={3}
        pt={matches({ md: 9.5, lg: 19 })}
        pb={11.5}
        px={0}
        zIndex={2}
      >
        <Typography
          textAlign={matches({ md: 'center', lg: 'start' })}
          weight={matches({ md: 'bolder', lg: 'bold' })}
          component="p"
          variant="h4"
        >
          {matches({
            md: 'بازار ارزهای دیجیتال',
            lg: 'قیمت لحظه‌ای ارزهای دیجیتال'
          })}
        </Typography>
        <Typography
          textAlign={matches({ md: 'center', lg: 'start' })}
          variant="body1"
          color="textColor.A60"
        >
          قیمت ارزهای دیجیتال را آنلاین مشاهده کنید.
        </Typography>
      </Stack>
      <Stack
        flexDirection="row"
        gap={4}
        sx={{ display: matches({ md: 'none', lg: 'flex' }) }}
        zIndex={2}
      >
        <TrendCrypto />
        <TopGainers />
        <TopLosers />
        <RecentlyAdded />
      </Stack>
      <Stack
        flexDirection="row"
        gap={4}
        sx={{ display: matches({ md: 'flex', lg: 'none' }) }}
        zIndex={2}
      >
        <SliderContent
          uniqueId="trending"
          itemsProps={{
            sx: {
              m: 1,
              width: '82.1%',
              '&:first-child': { marginLeft: 4 },
              '&:last-child': { marginRight: 4 }
            }
          }}
        >
          <TrendCrypto />
          <TopGainers />
          <TopLosers />
          <RecentlyAdded />
        </SliderContent>
      </Stack>
    </Stack>
  );
};

export default Trending;
